import { isEmpty, isNil, sortBy } from 'lodash-es';
import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { ConversationMessageContentType, type ConversationMessageTypeUnion } from '@/models/conversations/conversation';
import type { ConversationType } from '@/models/conversations/conversation-session';
import { messageManager } from '@/services/message-manager';
import { reportStorageError } from '@/services/storage-manager';
import { getUserId } from '@/stores/auth';
import { useConfigurationStore } from '@/stores/configuration';
import { usePeriodicMessageStore } from '@/stores/periodic-message';
import { isBurned, isBurning, isDeleted } from '@/utils/check-messages-status';
import { timeoutPromise } from '@/utils/promise';
import { generateSessionKey, generateSessionKeyFromMessage } from '@/utils/session';

interface MessageStoreState {
  key: string;

  listKey?: number;

  isHighlighted: boolean;

  searchId?: number;

  hydrated: boolean;

  unreadMessageCount: number;

  removedMessageIds: Map<number, number>;

  missingMessageIds: Set<number>;

  messages: Map<number, ConversationMessageTypeUnion>;
}

interface MessageStoreActions {
  clear(type: ConversationType, id: number): void;

  clearAll(): void;

  resetKey(): void;

  add(message: ConversationMessageTypeUnion): Promise<ConversationMessageTypeUnion>;

  addAll(messages: ConversationMessageTypeUnion[]): Promise<ConversationMessageTypeUnion[]>;

  deleteMany(
    messages: Array<ConversationMessageTypeUnion | number>,
    type: ConversationType,
    id: Nullable<number>,
  ): void;

  delete(message: ConversationMessageTypeUnion | number, type: ConversationType): void;

  deleteByUserId(userId: number, type?: ConversationType, sessionId?: number): Promise<void>;

  deleteBySessionId(type: ConversationType, id: number): Promise<void>;

  unmarkStale(message: ConversationMessageTypeUnion): ConversationMessageTypeUnion;

  hideAll(type: ConversationType, id: number): Promise<number[]>;

  validate(type: ConversationType, id: number, validateId: number, messageId: number): void;

  read(messageId: number, type: ConversationType, fromCounterpart?: boolean): void;

  readAll(type: ConversationType, id: number, fromCounterpart?: boolean): void;

  trim(): Promise<void>;

  hydrate(options: { type: ConversationType; id: number; searchId?: number }): Promise<void>;

  dehydrate(key?: string): void;
}

type MessageStore = MessageStoreState & MessageStoreActions;

export const useMessageStore = create<MessageStore>()(
  immer(
    (set, get): MessageStore => ({
      key: 'default' as string,

      searchId: undefined,

      isHighlighted: false,

      listKey: undefined,

      hydrated: false as boolean,

      messages: new Map<number, ConversationMessageTypeUnion>(),

      unreadMessageCount: 0 as number,

      removedMessageIds: new Map<number, number>(),

      missingMessageIds: new Set<number>(),

      async hydrate({ type, id, searchId }) {
        const { key: currentKey } = get();
        const key = generateSessionKey(type, id);

        if (key !== currentKey) {
          get().dehydrate(key);
        }

        let messages: ConversationMessageTypeUnion[] = [];

        if (!searchId) {
          messages = await timeoutPromise(
            () =>
              messageManager
                .dispatch('getMessages', type, id, 200)
                .then((messages) => messages)
                .catch((err) => {
                  reportStorageError(err, 'messages', 'hydrate', 'dexie-worker');
                  return [];
                }),
            6_000,
            () => [] as ConversationMessageTypeUnion[],
          );
        }

        let unreadMessageCount = 0;
        const missingMessageIds = checkMissingMessages(messages);
        const removedMessageIds = new Map<number, number>();
        const messageMap = new Map<number, ConversationMessageTypeUnion>();

        for (let i = messages.length - 1; i >= 0; i--) {
          const currentItem = messages.at(i);

          if (!currentItem) continue;

          if (isDeleted(currentItem)) {
            removedMessageIds.set(currentItem.SeqID, currentItem.ID);
            usePeriodicMessageStore.getState().addDeletedMessageId(key, currentItem.ID);
          } else {
            messageMap.set(currentItem.ID, currentItem);
          }

          if (!currentItem.IsRead) unreadMessageCount++;
        }

        set({
          key,
          hydrated: true,
          removedMessageIds,
          missingMessageIds,
          messages: messageMap,
          unreadMessageCount,
          searchId,
          isHighlighted: false,
        });
      },
      // [CHAT-2592] 傳入sessionKey，避免桌面版聊天室切換時會有default狀態，導致畫面閃動和倚賴key的remote data不正常更新
      dehydrate(key?: string) {
        const { messages } = get();

        if (messages.size > 0) {
          messageManager.queue('putMessages', Array.from(messages.values())).catch((err) => {
            reportStorageError(err, 'messages', 'dehydrate', 'dexie-worker');
          });
        }

        set({
          key: key || 'default',
          hydrated: false,
          removedMessageIds: new Map(),
          missingMessageIds: new Set(),
          messages: new Map(),
          unreadMessageCount: 0,
        });
      },

      clearAll() {
        messageManager
          .queue('clearAllMessages')
          .catch((err) => reportStorageError(err, 'messages', 'clearAllMessages', 'dexie-worker'));

        set({
          key: 'default',
          hydrated: false,
          removedMessageIds: new Map(),
          missingMessageIds: new Set(),
          messages: new Map(),
          unreadMessageCount: 0,
        });
      },

      clear(type, id) {
        const key = get().key;

        if (key === generateSessionKey(type, id)) {
          set({
            key: 'default',
            hydrated: false,
            removedMessageIds: new Map(),
            missingMessageIds: new Set(),
            messages: new Map(),
            unreadMessageCount: 0,
          });
        }

        messageManager
          .queue('clearMessagesBySession', type, id)
          .catch((err) => reportStorageError(err, 'messages', 'clear', 'dexie-worker'));
      },

      resetKey() {
        set({
          key: 'default',
          hydrated: false,
          messages: new Map(),
          removedMessageIds: new Map(),
          missingMessageIds: new Set(),
          unreadMessageCount: 0,
        });
      },

      async add(message) {
        const { key, messages, removedMessageIds } = get();

        const messageKey =
          'GroupID' in message
            ? generateSessionKey('group', message.GroupID)
            : generateSessionKey('session', message.ChatSessionID);
        const storedMessage = messages.get(message.TimeStamp);

        message.Message ||= storedMessage?.Message || '';
        message.IsRead = storedMessage?.IsRead || message.IsRead;
        message.ReadStamp = storedMessage?.ReadStamp || message.ReadStamp;

        if (key === messageKey) {
          set((draft) => {
            draft.messages.delete(message.TimeStamp);

            if (isDeleted(message)) {
              draft.removedMessageIds.set(message.SeqID, message.ID);
              usePeriodicMessageStore.getState().addDeletedMessageId(key, message.ID);
            } else if (!removedMessageIds.has(message.ID)) {
              draft.messages.set(message.ID, message);
            }

            const newMessage = draft.messages.get(message.ID);
            const missingMessageIds = checkMissingMessages([newMessage, message], draft.removedMessageIds);
            for (const id of missingMessageIds.keys()) {
              draft.missingMessageIds.add(id);
            }
          });
        }

        await messageManager
          .queue('addMessage', message)
          .catch((err) => reportStorageError(err, 'messages', 'add', 'dexie-worker'));

        return message;
      },
      async addAll(newMessages) {
        const { key, messages } = get();
        const headMessage = newMessages.at(0);
        if (!headMessage) return [];
        const messageKey = generateSessionKeyFromMessage(headMessage);
        const isMixedSessions = newMessages.some((m) => generateSessionKeyFromMessage(m) !== messageKey);

        if (key === messageKey && !isMixedSessions) {
          set((draft) => {
            for (const m of newMessages) {
              if (isDeleted(m)) {
                draft.messages.delete(m.ID);
                draft.removedMessageIds.set(m.SeqID, m.ID);
                usePeriodicMessageStore.getState().addDeletedMessageId(key, m.ID);
              } else {
                const message = messages.get(m.ID);
                m.Message ||= (m.ReadDelete ? message?.Message : m.Message) || '';
                m.IsRead = message?.IsRead || m.IsRead;
                m.ReadStamp = message?.ReadStamp || m.ReadStamp;

                if (isBurning(m)) {
                  usePeriodicMessageStore.getState().addBurnableMessage(m);
                }
                draft.messages.set(m.ID, m);
              }
            }
            const messageCollection = Array.from(draft.messages.values());

            draft.missingMessageIds = checkMissingMessages(messageCollection, draft.removedMessageIds);

            const lastNewMessageSeqId = Math.max(...newMessages.map((m) => m.SeqID).filter((v) => !isNil(v)));
            const lastStoredMessageSeqId = messageCollection.at(-1)?.SeqID ?? 0;

            if (lastNewMessageSeqId > 0 && lastStoredMessageSeqId > 0) {
              draft.unreadMessageCount = Math.max(0, lastNewMessageSeqId - lastStoredMessageSeqId);
            }
          });
        } else {
          // NOTE: put in store for messages match the store session
          if (key !== 'default') {
            const matchedMessages = newMessages.filter((m) => generateSessionKeyFromMessage(m) === key);
            if (matchedMessages) await get().addAll(matchedMessages);
          }
        }
        // TODO: 將阻擋訊息儲存的判斷在fetchMessage中藉由insertion處理
        //       目前在這裡處理，是因為在useMessageHistory的MessageHistoryInfiniteQuery以是否有messageId作為判斷，會導致定位偏移
        // 搜尋模式時不儲存messages, 避免直接切換成非搜尋模式時，initial的200筆訊息不是最新觸發長時間補訊息
        if (!get().searchId)
          await messageManager
            .queue('addMessages', newMessages)
            .catch((err) => reportStorageError(err, 'messages', 'addAll', 'dexie-worker'));

        return newMessages;
      },

      delete(message, type) {
        const key = typeof message === 'number' ? get().key : generateSessionKeyFromMessage(message);
        const messageId = typeof message === 'number' ? message : message.ID;

        usePeriodicMessageStore.getState().addDeletedMessageId(key, messageId);

        if (key === get().key) {
          set((draft) => {
            const deletingMessage = draft.messages.get(messageId);

            if (deletingMessage) {
              draft.messages.delete(messageId);
              draft.removedMessageIds.set(deletingMessage.SeqID, messageId);
            }
          });
        }

        messageManager
          .queue('deleteMessage', type, messageId)
          .catch((err) => reportStorageError(err, 'messages', 'delete', 'dexie-worker'));
      },

      deleteMany(messages, type, sessionId) {
        const messageIds = messages.map((m) => (typeof m === 'number' ? m : m.ID));
        const sessionKey = generateSessionKey(type, sessionId);

        if (!isEmpty(messages)) {
          messageManager
            .dispatch('deleteMessages', type, messageIds)
            .then((messages) => {
              for (const m of messages) {
                if (m) {
                  if (!isBurned(m)) continue;
                  usePeriodicMessageStore.getState().addDeletedMessageId(sessionKey, m.ID);
                }
              }
            })
            .catch((err) => reportStorageError(err, 'messages', 'deleteMessages', 'dexie-worker'));
        }

        set((draft) => {
          for (const messageId of messageIds) {
            const m = draft.messages.get(messageId);
            if (m) {
              if (!isBurned(m)) continue;
              usePeriodicMessageStore.getState().addDeletedMessageId(sessionKey, m.ID);
              draft.messages.delete(m.ID);
              draft.removedMessageIds.set(m.SeqID, m.ID);
            }
          }
        });
      },

      async deleteBySessionId(type, id) {
        const deletedMessages = await messageManager.dispatch('deleteMessagesBySession', type, id).catch((err) => {
          reportStorageError(err, 'messages', 'deleteBySessionId', 'dexie-worker');
          return [] as ConversationMessageTypeUnion[];
        });

        const sessionKey = generateSessionKey(type, id);

        set((draft) => {
          for (const m of deletedMessages) {
            usePeriodicMessageStore.getState().addDeletedMessageId(sessionKey, id);
            draft.messages.delete(m.ID);
            draft.removedMessageIds.set(m.SeqID, m.ID);
          }
        });
      },

      async deleteByUserId(userId, type, id) {
        const deletedMessages = await messageManager.queue('deleteMessagesByUser', userId, type, id).catch((err) => {
          reportStorageError(err, 'messages', 'deleteMessagesByUser', 'dexie-worker');
          return [] as ConversationMessageTypeUnion[];
        });

        set((draft) => {
          for (const m of deletedMessages) {
            usePeriodicMessageStore.getState().addDeletedMessageId(generateSessionKeyFromMessage(m), m.ID);
            draft.messages.delete(m.ID);
            draft.removedMessageIds.set(m.SeqID, m.ID);
          }
        });
      },

      validate(type, id, validateId, messageId) {
        const { key, messages } = get();
        const messageKey = generateSessionKey(type, id);

        const validatingMessage = messages.get(validateId);
        if (!validatingMessage) return;

        const message = { ...validatingMessage, Status: true, ID: messageId };

        if (key === messageKey) {
          set((draft) => {
            draft.messages.delete(validateId);
            draft.messages.set(messageId, message);
          });
        }

        messageManager
          .queue('validateMessage', type, validateId, message)
          .catch((err) => reportStorageError(err, 'messages', 'validate', 'dexie-worker'));
      },

      read(messageId, type, fromCounterpart = false) {
        const { messages } = get();
        const readStamp = Date.now();
        const userId = getUserId();
        const message = messages.get(messageId);

        if (message) {
          if (
            message.ReadDelete &&
            message.MsgType !== ConversationMessageContentType.Notice &&
            (fromCounterpart ? message.UserID === userId : message.UserID !== userId)
          ) {
            usePeriodicMessageStore.getState().addBurnableMessage({ ...message, IsRead: true, ReadStamp: readStamp });
          }

          set((draft) => {
            const m = draft.messages.get(messageId);
            if (m) {
              m.IsRead = true;
              m.ReadStamp ||= readStamp;
            }
            if (draft.unreadMessageCount >= 1) draft.unreadMessageCount--;
          });
        }

        messageManager
          .dispatch('markMessageRead', type, messageId, readStamp, fromCounterpart)
          .then((m) => {
            if (
              m?.ReadDelete &&
              m.MsgType !== ConversationMessageContentType.Notice &&
              (fromCounterpart ? m.UserID === userId : m.UserID !== userId)
            ) {
              usePeriodicMessageStore.getState().addBurnableMessage(m);
            }
          })
          .catch((err) => reportStorageError(err, 'messages', 'markMessageRead', 'dexie-worker'));
      },
      readAll(type, id, fromCounterpart = false) {
        const { key, messages } = get();
        const readStamp = Date.now();
        const userId = getUserId();

        if (key === generateSessionKey(type, id)) {
          set((draft) => {
            for (const message of messages.values()) {
              if (!message) continue;
              if (fromCounterpart ? type === 'group' : type === 'session') continue;

              if (
                message.ReadDelete &&
                message.MsgType !== ConversationMessageContentType.Notice &&
                (fromCounterpart ? message.UserID === userId : message.UserID !== userId)
              ) {
                usePeriodicMessageStore
                  .getState()
                  .addBurnableMessage({ ...message, IsRead: true, ReadStamp: message.ReadStamp || readStamp });
              }

              const draftMessage = draft.messages.get(message.ID);
              if (draftMessage) {
                draftMessage.IsRead = true;
                draftMessage.ReadStamp ||= readStamp;
              }
            }
            draft.unreadMessageCount = 0;
          });
        }

        messageManager
          .dispatch('markMessagesReadBySession', type, id, readStamp, fromCounterpart)
          .then((messages) => {
            for (const m of messages) {
              usePeriodicMessageStore.getState().addBurnableMessage(m);
            }
          })
          .catch((err) => reportStorageError(err, 'messages', 'markMessagesReadBySession', 'dexie-worker'));
      },

      async hideAll(type, id) {
        const { key, messages } = get();
        const hidingKey = generateSessionKey(type, id);

        if (key === hidingKey) {
          set((draft) => {
            for (const m of messages.values()) {
              if (m) {
                draft.messages.delete(m.ID);
                draft.removedMessageIds.set(m.SeqID, m.ID);
              }
            }
          });

          const maxMessageId = Array.from(messages.values()).reduce(
            (accu, m) => (m.Status !== null && m.ID > accu ? m.ID : accu),
            0,
          );
          usePeriodicMessageStore.getState().setPurgedMessageId(hidingKey, maxMessageId);
        }

        return messageManager
          .dispatch('hideMessages', type, id)
          .then((messageIds) => {
            usePeriodicMessageStore.getState().setPurgedMessageId(hidingKey, Math.max(...messageIds));

            return messageIds;
          })
          .catch((err) => {
            reportStorageError(err, 'messages', 'hideMessages', 'dexie-worker');
            return [];
          });
      },

      unmarkStale(message) {
        const { messages } = get();

        const nowTime = Date.now();
        const newMessage = {
          ...message,
          ID: nowTime,
          TimeStamp: nowTime,
          CreateTime: nowTime,
        };

        if (messages.has(message.TimeStamp)) {
          set((draft) => {
            draft.messages.delete(message.TimeStamp);
            draft.messages.set(nowTime, newMessage);
          });
        }

        messageManager
          .queue('unmarkMessageStale', newMessage, message.TimeStamp)
          .catch((err) => reportStorageError(err, 'messages', 'unmarkMessageStale', 'dexie-worker'));

        return newMessage;
      },

      async trim() {
        const retentionTime =
          useConfigurationStore.getState().envConfig.config?.messageRetentionTime ?? 1000 * 60 * 60 * 24 * 7;

        await timeoutPromise(() => messageManager.queue('trimMessages', retentionTime), 10_000).catch((err) =>
          reportStorageError(err, 'messages', 'trim', 'dexie-worker'),
        );
      },
    }),
  ),
);

export function checkMissingMessages(
  messages: Array<Nullable<ConversationMessageTypeUnion>>,
  removedIds = new Map<number, number>(),
): Set<number> {
  if (messages.length < 2) return new Set();

  const seqIdMap = messages.reduce<Record<number, number>>((accu, curr) => {
    if (!isNil(curr) && curr.ID !== curr.TimeStamp && curr.SeqID > 0) {
      accu[curr.SeqID] = curr.ID;
    }

    return accu;
  }, {});

  const sortedMap = sortBy(
    Object.entries(seqIdMap).map(([seqId, id]): [number, number] => [Number(seqId), id]),
    ([seqId]) => seqId,
  );

  const missingMessageIds = new Set<number>();

  if (sortedMap.length >= 2) {
    for (let i = 0; i < sortedMap.length; i++) {
      const [currentSeqId] = sortedMap.at(i) ?? [-1, 0];
      const [nextSeqId, nextId] = sortedMap.at(i + 1) ?? [-1, 0];
      if (nextSeqId > 0 && nextSeqId - currentSeqId !== 1 && !removedIds.has(currentSeqId + 1)) {
        missingMessageIds.add(nextId);
      }
    }
  }

  return missingMessageIds;
}

export const addMessages = useMessageStore.getState().addAll;

export const trimMessages = useMessageStore.getState().trim;
